import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import { useGetDocumentRowValuesCount } from '@/service-library/hooks/document-row-values'
import { useGetDocumentsCount } from '@/service-library/hooks/documents'
import {
  useTrainModel,
  useCancelTrainingModel,
} from '@/service-library/hooks/model-train'
import { useGetProjectModelTrainingFields } from '@/service-library/hooks/project-model-training-fields'
import {
  useGetProjectModel,
  useGetProjectModels,
} from '@/service-library/hooks/project-models'
import { useGetPubModels } from '@/service-library/hooks/pub-models'
import useIsSuperUserVisible from '@/services/hooks/useIsSuperUserVisible'
import { ProjectModel } from '@/types/project-models'
import {
  CATEGORY_MODEL_TYPE_ID,
  getCurrentVersion,
  getNewFieldOccurrencesCount,
  getNewItemOccurrencesCount,
  getTrainingVersion,
  getVersionInCancelingState,
} from '@/utils/model-page'
import { showErrorSnackbar } from '@/utils/snackbars'
import { convertSecondsToHours, prettifyDate } from '@/utils/time'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import GeneralInfoBox, {
  GeneralInfoBoxContent,
} from '@/components/data-visualization/GeneralInfoBox'
import PercentageColorBar from '@/components/data-visualization/PercentageColorBar'
import ListAutocomplete from '@/components/list-autocomplete/ListAutocomplete'
import ModelVersionSelect from '@/components/model-version-select/ModelVersionSelect'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import PixelGridBackground from '@/components/pixel-grid-background/PixelGridBackground'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import CategoryItemCountChartCard from './CategoryItemCountChartCard'
import FieldAccuracyChartCard from './FieldAccuracyChartCard'
import FieldCountComparisonChartCard from './FieldCountComparisonChartCard'
import HandleTrainingButton from './HandleTrainingButton'
import ModelVersionAccuracyCard from './ModelVersionAccuracyCard'
import ModelVersionFieldAccuracyCard from './ModelVersionFieldAccuracyCard'
import NotTrainedBanner from './NotTrainedBanner'
import TrainInfoBanner from './TrainInfoBanner'

export default function ModelDashboard() {
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const navigate = useNavigate()
  const isSuperUserVisible = useIsSuperUserVisible()

  const { projectModels } = useGetProjectModels({
    filters: {
      limit: '1000',
      fields__only: 'id,name,data_list_id',
      training_workflow_id: selectedWorkflow.id,
    },
  })

  const rootModel = useMemo(
    () => projectModels.find((model) => !model.parent_model_id),
    [projectModels],
  )
  const { value: selectedModelId, toggleValue: setSelectedModelId } =
    useStatefulSearchParam({
      param: 'project_model',
      defaultValue: rootModel?.id,
    })
  const selectedModel = projectModels.find(({ id }) => id === selectedModelId)

  // If the model is missing for some reason, set to the root model.
  useEffect(() => {
    if (rootModel && !selectedModel) {
      setSelectedModelId(rootModel.id)
    }
  }, [rootModel, selectedModel, setSelectedModelId])

  const { pubModels } = useGetPubModels({
    filters: {
      limit: '1',
      project_model_id: rootModel?.id,
      fields__only: 'id',
    },
    enabled: !!rootModel,
  })

  const {
    projectModel: currentModel,
    refetch,
    queryKey,
  } = useGetProjectModel({
    id: selectedModelId || '',
    filters: {
      fields__include:
        'project_model_versions,project_grid_fields,has_children',
      project_model_versions__fields__include:
        'field_stats,category_stats,project_model_version_fields',
    },
  })

  const availableForTrainingState = selectedWorkflow?.workflow_states?.find(
    ({ code }) => code === 'available_for_training',
  )
  const trainingBatchState = selectedWorkflow?.workflow_states?.find(
    ({ code }) => code === 'training_batch',
  )

  const { groupedCounts } = useGetDocumentsCount({
    filters: {
      '~group-by': 'workflow_states__code',
      workflow_states__id__in: `${availableForTrainingState?.id},${trainingBatchState?.id}`,
      self_and_descendants_of_owner_org_id: currentModel?.org_id || '',
    },
    enabled:
      !!currentModel?.id && !!availableForTrainingState && !!trainingBatchState,
  })

  const countByStateCode = useMemo(() => {
    return groupedCounts.reduce<Record<string, number>>(
      (acc, { workflow_states__code, count }) => {
        acc[workflow_states__code] = count
        return acc
      },
      {},
    )
  }, [groupedCounts])

  const isCategoryModel =
    currentModel?.project_model_type_id === CATEGORY_MODEL_TYPE_ID

  const { projectModelTrainingFields } = useGetProjectModelTrainingFields({
    filters: {
      limit: '1000',
      project_model_id: rootModel?.id,
    },
    enabled: isCategoryModel && rootModel?.data_list_id !== null,
  })

  const pickerFieldId = projectModelTrainingFields.find(
    ({ flow }) => flow === 'out',
  )?.project_grid_field_id

  const { documentRowValuesCount: trainingBatchCountPerField } =
    useGetDocumentRowValuesCount({
      filters: {
        document_row__document__self_and_descendants_of_owner_org_id:
          currentModel?.org_id || '',
        document_row__document__document_workflow_states__workflow_state_id:
          trainingBatchState?.id,
      },
      enabled: !!trainingBatchState && !isCategoryModel,
    })

  const { documentRowValuesCount: trainingBatchCountPerItem } =
    useGetDocumentRowValuesCount({
      filters: {
        document_row__document__self_and_descendants_of_owner_org_id:
          currentModel?.org_id || '',
        document_row__document__document_workflow_states__workflow_state_id:
          trainingBatchState?.id,
        project_grid_field_id: pickerFieldId,
        final_ref_id__isnull: 'false',
      },
      enabled: !!trainingBatchState && !!pickerFieldId,
    })

  const modelVersions = useMemo(
    () => currentModel?.project_model_versions || [],
    [currentModel?.project_model_versions],
  )

  const sortedVersions = modelVersions.sort((a, b) => {
    return a.version - b.version
  })

  const [selectedVersionId, setSelectedVersionId] = useState<string>('')

  const { trainModel } = useTrainModel({
    detailQueryKey: queryKey,
    onMutate: () => {
      setSelectedVersionId('training')
    },
  })

  const { cancelTrainingModel } = useCancelTrainingModel({
    detailQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Failed to cancel training. Please try again later.')
    },
  })

  const selectedVersion = useMemo(
    () => modelVersions.find(({ id }) => id === selectedVersionId),
    [modelVersions, selectedVersionId],
  )

  const currentVersion = useMemo(
    () => getCurrentVersion(modelVersions),
    [modelVersions],
  )

  const lastVersion = sortedVersions.at(-1)

  const trainingVersion = useMemo(
    () => getTrainingVersion(modelVersions),
    [modelVersions],
  )

  const versionInCancelingState = useMemo(
    () => getVersionInCancelingState(modelVersions),
    [modelVersions],
  )

  function handleCancelOrTrainModel() {
    if (trainingVersion) {
      cancelTrainingModel({ project_model_version_id: trainingVersion.id })
    } else if (currentModel) {
      trainModel(currentModel)
    }
  }

  useEffect(() => {
    if (modelVersions.length === 0 || selectedVersion) return
    setSelectedVersionId(currentVersion?.id || lastVersion?.id || '')
  }, [
    currentVersion?.id,
    lastVersion?.id,
    modelVersions.length,
    selectedVersion,
  ])

  useEffect(() => {
    if (selectedVersionId === 'training' && trainingVersion?.id) {
      setSelectedVersionId(trainingVersion?.id)
    }
  }, [selectedVersionId, trainingVersion?.id])

  useEffect(() => {
    if (selectedWorkflow && !selectedWorkflow?.project_model_id) {
      navigate('..', { relative: 'path' })
    }
  })

  useNotifications({
    keys: ['training_status'],
    callback: ({ model_version }) => {
      if (
        currentModel &&
        model_version &&
        model_version.project_model_id === currentModel.id
      ) {
        if (
          model_version.training_status === 'trained' ||
          model_version.training_status === 'error' ||
          model_version.training_status === 'canceled'
        )
          refetch()
        if (model_version.training_status === 'error')
          showErrorSnackbar('Training Failed')
      }
    },
  })

  const largeNumberOfFields = selectedVersion
    ? (selectedVersion.field_stats?.total_count || 0) > 50
    : false

  const largeNumberOfCategories = selectedVersion
    ? (selectedVersion.category_stats?.total_count || 0) > 50
    : false

  const canTrain =
    currentModel &&
    // Not currently training
    !trainingVersion &&
    // Not currently canceling training
    !versionInCancelingState &&
    // Must have at least 10 documents in the training batch
    (countByStateCode.training_batch || 0) >= 10 &&
    // Is current version, or...
    (selectedVersion?.is_current ||
      // There is no current version and this is the most recent version
      (!currentVersion && selectedVersion?.id === lastVersion?.id))

  const estimatedAccuracy = selectedVersion?.accuracy
    ? `${Math.floor(+selectedVersion.accuracy * 100)}%`
    : '0%'

  const newFieldOccurrencesCount =
    selectedVersion &&
    selectedVersion.training_status !== 'training' &&
    !isCategoryModel
      ? getNewFieldOccurrencesCount(trainingBatchCountPerField, selectedVersion)
      : 0

  const newItemOccurrencesCount =
    selectedVersion &&
    selectedVersion.training_status !== 'training' &&
    isCategoryModel
      ? getNewItemOccurrencesCount(trainingBatchCountPerItem, selectedVersion)
      : 0

  return projectModels.length ? (
    <>
      <PixelGridBackground animateBackground={!!trainingVersion} />

      <Box sx={{ p: 4, width: '100%', maxWidth: 2000, m: '0 auto' }}>
        <ProjectBreadcrumb label="Dashboard" url="." />

        <Stack spacing={2} sx={{ zIndex: 1 }}>
          {pubModels.length > 0 && (
            <Alert severity="info" variant="filled">
              This is a published model. Any newly trained version will only
              become available in other projects once the new version is
              published.
            </Alert>
          )}

          {selectedModel && rootModel && (
            <Grid container spacing={2}>
              <Grid size={12}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {projectModels.length > 1 ? (
                    <ListAutocomplete<ProjectModel>
                      options={projectModels}
                      selected={selectedModel || null}
                      setSelected={(model) => setSelectedModelId(model.id)}
                      label="Model"
                      sx={{ width: 250 }}
                    />
                  ) : (
                    <Typography component="h1" variant="h6">
                      {selectedModel.name}
                    </Typography>
                  )}
                  {currentModel &&
                  (!modelVersions.length || selectedVersionId) ? (
                    <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
                      {isSuperUserVisible && (
                        <>
                          <CopyIDButton
                            stringToCopy={currentModel.id}
                            isSuperUser
                            label="Model ID"
                          />
                          {selectedVersion && (
                            <CopyIDButton
                              stringToCopy={selectedVersion.id}
                              isSuperUser
                              label="Version ID"
                            />
                          )}
                        </>
                      )}
                      <ModelVersionSelect
                        selectedVersionId={selectedVersionId || 'no-versions'}
                        setSelectedVersionId={setSelectedVersionId}
                        modelVersions={modelVersions}
                      />
                    </Stack>
                  ) : (
                    <Skeleton height={50} width={100} variant="rounded" />
                  )}
                </Stack>
              </Grid>
              {currentModel ? (
                <>
                  {modelVersions.length === 0 && (
                    <Grid size={12}>
                      <NotTrainedBanner />
                    </Grid>
                  )}
                  {(selectedVersion?.training_status === 'error' ||
                    selectedVersion?.training_status === 'canceled') && (
                    <Grid size={12}>
                      <TrainInfoBanner
                        modelVersion={selectedVersion}
                        onDelete={() => {
                          setSelectedVersionId(
                            currentVersion?.id || lastVersion?.id || '',
                          )
                        }}
                      />
                    </Grid>
                  )}

                  {/* ------------------------------- */}
                  {/* ----------INFO BOXES----------- */}
                  {/* ------------------------------- */}

                  {/* Training Button */}
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <HandleTrainingButton
                      onClick={handleCancelOrTrainModel}
                      status={
                        trainingVersion
                          ? 'training'
                          : versionInCancelingState
                          ? 'canceling'
                          : undefined
                      }
                      disabled={
                        trainingVersion
                          ? selectedVersion?.id !== trainingVersion.id
                          : !canTrain
                      }
                      newOccurrencesCount={
                        newFieldOccurrencesCount || newItemOccurrencesCount
                      }
                      model={currentModel}
                    />
                  </Grid>

                  {/* Estimated Accuracy */}
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <GeneralInfoBox
                      label="Estimated Accuracy"
                      value={
                        selectedVersion?.id === trainingVersion?.id
                          ? '---'
                          : estimatedAccuracy
                      }
                    >
                      {selectedVersion && (
                        <PercentageColorBar
                          percentage={
                            selectedVersion.accuracy
                              ? +selectedVersion.accuracy * 100
                              : 0
                          }
                        />
                      )}
                    </GeneralInfoBox>
                  </Grid>

                  {/* Model Version */}
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <GeneralInfoBox
                      label={
                        <Stack>
                          <Typography component="h2" variant="body2">
                            Model Version
                          </Typography>
                          <Typography variant="subtitle1">
                            {selectedVersion
                              ? prettifyDate(
                                  selectedVersion.training_completed_at ||
                                    selectedVersion.training_started_at,
                                )
                              : '---'}
                          </Typography>
                        </Stack>
                      }
                      value={
                        selectedVersion ? `${selectedVersion.version}.0` : '0.0'
                      }
                      sx={{ pb: 1 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Card
                      elevation={2}
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: 1.5,
                        height: '100%',
                      }}
                    >
                      <GeneralInfoBoxContent
                        label="Documents Used to Train"
                        shrinkValueSize
                        value={
                          selectedVersion?.id === trainingVersion?.id
                            ? '---'
                            : selectedVersion?.document_count || '---'
                        }
                      />
                      <GeneralInfoBoxContent
                        label="Training Duration"
                        shrinkValueSize
                        value={
                          selectedVersion &&
                          selectedVersion.id !== trainingVersion?.id &&
                          selectedVersion.training_started_at &&
                          selectedVersion.training_completed_at
                            ? convertSecondsToHours(
                                Math.round(
                                  (new Date(
                                    selectedVersion.training_completed_at,
                                  ).getTime() -
                                    new Date(
                                      selectedVersion.training_started_at,
                                    ).getTime()) /
                                    1000,
                                ),
                              )
                            : '---'
                        }
                      />
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Card
                      elevation={2}
                      sx={{
                        height: '100%',
                      }}
                    >
                      {/* Number in Available to Train */}
                      <CardActionArea
                        component={Link}
                        to={`../data?workflow=${selectedWorkflow.id}&workflow_state=${availableForTrainingState?.id}`}
                        state={{ org_id: currentModel?.org_id }}
                        sx={{ px: 1.5, pt: 1.5, pb: 0.75 }}
                      >
                        <GeneralInfoBoxContent
                          label="Available to Train"
                          shrinkValueSize
                          value={countByStateCode.available_for_training || 0}
                        />
                      </CardActionArea>

                      {/* Number in Training Batch */}
                      <CardActionArea
                        component={Link}
                        to={`../data?workflow=${selectedWorkflow.id}&workflow_state=${trainingBatchState?.id}`}
                        state={{ org_id: currentModel?.org_id }}
                        sx={{ px: 1.5, pb: 1.5, pt: 0.75 }}
                      >
                        <GeneralInfoBoxContent
                          label="Training Batch"
                          shrinkValueSize
                          value={countByStateCode.training_batch || 0}
                        />
                      </CardActionArea>
                    </Card>
                  </Grid>
                  {/* --------------------------- */}
                  {/* ----------CHARTS----------- */}
                  {/* --------------------------- */}

                  {/* Field Count Comparison Chart */}
                  {!isCategoryModel && selectedVersion && (
                    <Grid size={{ xs: 12, md: largeNumberOfFields ? 12 : 7 }}>
                      <FieldCountComparisonChartCard
                        currentVersion={selectedVersion}
                      />
                    </Grid>
                  )}

                  {isCategoryModel && selectedVersion && (
                    <Grid
                      size={{ xs: 12, md: largeNumberOfCategories ? 12 : 7 }}
                    >
                      <CategoryItemCountChartCard
                        modelVersion={selectedVersion}
                      />
                    </Grid>
                  )}

                  {/* Model Versions Accuracy Chart */}
                  {selectedVersion && (
                    <Grid size={{ xs: 12, md: largeNumberOfFields ? 12 : 5 }}>
                      <ModelVersionAccuracyCard
                        modelVersions={sortedVersions}
                      />
                    </Grid>
                  )}

                  {/* Model Version Field Accuracy Chart */}
                  {selectedVersion && !isCategoryModel && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <ModelVersionFieldAccuracyCard
                        modelVersions={sortedVersions}
                      />
                    </Grid>
                  )}

                  {/* Field Accuracy Chart or Category Item Count */}
                  {selectedVersion && !isCategoryModel && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldAccuracyChartCard modelVersion={selectedVersion} />
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Skeleton height={120} variant="rounded" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Skeleton height={120} variant="rounded" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Skeleton height={120} variant="rounded" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Skeleton height={120} variant="rounded" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Skeleton height={120} variant="rounded" />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </Stack>
      </Box>
    </>
  ) : null
}
